import { ref, computed, markRaw, type Component } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useModal } from '@/apps/core/composables'
import { useToast } from '@/apps/core'
import { useWorkspacePermissions } from '../queries'
import { useUpload } from './useUpload'

/**
 * Wraps `useUpload` with a workspace picker fallback for views where the
 * route doesn't carry a workspace (e.g. "vue d'ensemble"). When a workspace
 * is available from the route, files are uploaded directly; otherwise a
 * modal asks the user to pick one before the upload starts.
 *
 * **The route's workspace is asked for `assets.asset.create` first**, so a drop on a workspace the
 * caller only reads is refused here, with a sentence, rather than queued for the server's 403. The
 * picker needs no such check: it offers only the workspaces that accept one.
 */
export function useUploadFlow() {
  const route = useRoute()
  const { t } = useI18n()
  const toast = useToast()
  const { open: openModal } = useModal()

  const pickedWorkspaceId = ref<string | undefined>(undefined)
  const { addFiles } = useUpload({ workspaceId: pickedWorkspaceId })

  function getRouteWorkspaceId(): string | null {
    const id = route.params.workspaceId as string | undefined
    return id === 'all' || !id ? null : id
  }

  const routeWorkspace = useWorkspacePermissions(() => getRouteWorkspaceId())

  /**
   * Whether a drop here would be accepted — always, where the picker chooses the workspace. While
   * the workspace or its role is still being read the drop is let through rather than refused
   * with a sentence that may be wrong: the server refuses it anyway if the role does not allow it.
   */
  const canUpload = computed(
    () => getRouteWorkspaceId() === null || routeWorkspace.isLoading.value || routeWorkspace.can('assets.asset.create'),
  )

  async function uploadFiles(files: File[]): Promise<void> {
    if (files.length === 0) return

    if (getRouteWorkspaceId()) {
      if (!canUpload.value) {
        toast.error(t('assets.upload.errors.notAllowed'))
        return
      }
      pickedWorkspaceId.value = undefined
      addFiles(files)
      return
    }

    const { default: UploadWorkspacePickerModal } = await import(
      '../modals/UploadWorkspacePickerModal.vue'
    )
    const result = await openModal<{ workspaceId: string }>(
      t('assets.upload.workspacePicker.title'),
      markRaw(UploadWorkspacePickerModal as Component),
      {
        width: 450,
        props: { fileCount: files.length },
      },
    )
    if (!result.confirmed || !result.data?.workspaceId) return
    pickedWorkspaceId.value = result.data.workspaceId
    addFiles(files)
  }

  return { uploadFiles, canUpload }
}
